/**
 * @file THE WEB AUDIO HALF — the `AudioContext`, the bus gain graph, the buffer
 * cache, the node pool and the loop-point scan. **It knows nothing about the
 * game**: no slugs, no distances, no priorities, no entities. It is handed a
 * URL, a bus name and four numbers, and it makes a noise.
 *
 * The split is `systems/Speech.js` (model, node-testable) against
 * `render/SpeechBubbles.js` (pixels), one layer down: `audio/mixer.js` decides
 * WHICH sound at WHAT gain, this file is the only thing in the project that has
 * ever heard of `AudioBufferSourceNode`, and `audio/Sfx.js` joins the two.
 * Nothing outside `Sfx.js` should import this file.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE GRAPH
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 *   source ─► gain ─► panner ─┬─► sfxGain ─────────────────┐
 *   (new per voice) (pooled)  ├─► ambientGain ─────────────┤
 *                             └─► uiGain ──────────────────┼─► masterGain ─► dest
 *                                                          │
 *   <audio> ─► mediaSource ─► trackGain ─► musicGain ───────┘
 *   └─── ONE element and ONE source node, for the whole session ───┘
 * ```
 * SIX `GainNode`s exist for the whole session — master, the three decoded buses,
 * the `music` bus and the music TRACK gain — and everything else is pooled. The
 * music path needs both of its gains: the bus one is the player's slider and the
 * track one is the radio's own level and its fades, and a fade in flight must not
 * be able to overwrite a slider, nor the other way round.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ `StereoPannerNode`, NEVER `PannerNode`
 * ─────────────────────────────────────────────────────────────────────────────
 * A `PannerNode` is an HRTF/equal-power spatialiser with a listener, a cone, a
 * distance model and a per-sample convolution behind it — an order of magnitude
 * more expensive per voice than a stereo pan, and it would duplicate the
 * distance law the mixer already owns in one pure function. This game budgets
 * two dozen simultaneous voices on a machine that is already inside a WebGPU
 * frame; a panner per gunshot is exactly the cost that budget cannot pay. The
 * fallback when `createStereoPanner` is missing (very old Safari) is a plain
 * pass-through gain and NO panning — never a `PannerNode`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THE POOL: REUSE THE GAIN AND THE PANNER, RECREATE ONLY THE SOURCE
 * ─────────────────────────────────────────────────────────────────────────────
 * The spec makes `AudioBufferSourceNode` single-use — once it has been started
 * and stopped it can never play again — so a new one per voice is unavoidable.
 * `GainNode` and `StereoPannerNode` carry no such restriction, and allocating
 * three nodes and tearing down three nodes for every footstep is precisely the
 * churn this design exists to avoid. A voice therefore takes a `{gain, panner}`
 * pair off a free list, re-points the panner at its bus, and hands the pair back
 * on `onended`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ MUSIC IS STREAMED, AND ITS SOURCE NODE IS BUILT ONCE PER ELEMENT, EVER
 * ─────────────────────────────────────────────────────────────────────────────
 * Everything above is a decoded `AudioBuffer`, which is right for a 0.3 s gunshot
 * and catastrophic for a song: three minutes of 48 kHz stereo is ~69 MB of
 * `Float32Array` once `decodeAudioData` has finished with it, and the radio holds
 * five of them. So a TRACK IS NOT A VOICE and never goes near the cache, the
 * queue, the pool or {@link api.play} — it is ONE detached `HTMLAudioElement` the
 * browser streams for us, routed in through `createMediaElementSource`. Seeking
 * then comes free with `el.currentTime`, which is exactly what a station that
 * kept playing while the player was out of the car needs.
 *
 * ⚠⚠ **`createMediaElementSource` MAY BE CALLED ONCE PER ELEMENT, EVER.** A
 * second call throws — and because the second call would happen where the second
 * SONG starts, the radio would die silently after track one. Hence
 * {@link wireMusic} and its `musicWired` latch, which is set BEFORE the call
 * rather than after it, so a call that threw is never retried either. Changing
 * track is `el.src = url`; it is never a new element and never a new source node.
 *
 * ⚠ The element's own `.volume` is written once, to 1, and never touched again.
 * Every level and every fade on this path is a ramp on a `GainNode`, for the same
 * reason as every other voice — and `.volume` cannot be ramped at all, so using
 * it would be a step change on a CONTINUOUS signal, which is the most obvious
 * artifact this system can produce (the ear has the rest of the track to compare
 * the discontinuity against).
 *
 * ⚠ `el.play()` is only ever called on a WIRED element. An unrouted element plays
 * straight to the speakers, escaping `masterGain` — so it would ignore the master
 * slider and the mute. Before `unlock()` there is no context and therefore no
 * route, so the intent is remembered (`musicWanted`) and `unlock()` re-asserts
 * it. The returned promise is caught and dropped, never awaited and never
 * rethrown: a rejection is the autoplay policy saying "not yet", not an error.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ NEVER `.value =` ON A LIVE VOICE
 * ─────────────────────────────────────────────────────────────────────────────
 * A step change on a running `AudioParam` is a discontinuity in the waveform,
 * and a discontinuity is a click. `tools/build-sfx.mjs` records the same fact
 * twice about the offline renders ("a click is the one artifact that survives
 * distance attenuation intact") — it survives distance attenuation because
 * clicks are broadband and the attenuation is a scalar. So every write to a live
 * param goes through {@link rampTo}, which enforces a floor on the ramp length;
 * a bare assignment is legal ONLY on a node that has not been connected to a
 * started source yet, and each of the three such places says so in a comment.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THE LOOP-POINT SCAN, AND WHY IT IS HERE AND NOT IN THE BUILD
 * ─────────────────────────────────────────────────────────────────────────────
 * MP3 carries encoder delay at the head and padding at the tail, so a looping
 * buffer played back naively repeats a few milliseconds of digital silence every
 * cycle — a hole punched into the rain bed and the siren, both of which run for
 * minutes. `tools/build-sfx.mjs` carries the long version of why this cannot be
 * measured at build time: measuring the WAV finds nothing (the padding does not
 * exist until LAME has run) and measuring the MP3 with ffmpeg finds nothing
 * either (ffmpeg honours LAME's gapless header and strips it on the way out).
 * **The only decoder whose padding matters is the one in the player's browser**,
 * so the only correct place to measure is the `AudioBuffer` it just produced.
 * `kind: 'loop'` in the generated manifest is what says which buffers are worth
 * the scan.
 *
 * @module engine/audio/AudioBackend
 */

/**
 * The three destination buses, listed in DECODE PRIORITY order. `ui` is first
 * because the phone can ring before the city has finished building — a title
 * screen that clicks and a phone that rings are the sounds a player meets before
 * any of the world's, and they are also the smallest files in the pack.
 */
const TIERS = Object.freeze(['ui', 'sfx', 'ambient']);

/**
 * Simultaneous `fetch` + `decodeAudioData` jobs. ⚠ CAPPED, and the cap is the
 * point: `decodeAudioData` is asynchronous but not free of the main thread on
 * every browser, and firing all 49 clips at once has been measured elsewhere to
 * stall the first frames of a level. Six keeps the network pipe full without
 * handing the decoder a burst it will serialise anyway.
 */
const DECODE_CONCURRENCY = 6;

/**
 * Maximum seconds trimmed off EACH END of a looping buffer. Encoder delay is
 * ~1152 samples (26 ms at 44.1 kHz) and padding is bounded by one frame more, so
 * 120 ms is roughly four times the worst case — generous enough to swallow any
 * decoder's idea of the margin, tight enough that it can never eat real audio.
 * ⚠ It is also what bounds the scan: only the first and last 120 ms are ever
 * examined, so scanning a 22 s rain bed costs ~10k samples, not a million.
 */
const LOOP_TRIM_MAX = 0.12;

/** Below this magnitude a sample is silence for the loop scan's purposes. */
const SILENCE = 1e-4;

/**
 * Shortest ramp {@link rampTo} will schedule, seconds. 5 ms is under the ear's
 * ability to hear the ramp itself and over the length of the click it replaces.
 * ⚠ It is a FLOOR, not a default: a caller asking for an instant change gets
 * 5 ms, because "instant" is the click.
 */
const MIN_RAMP = 0.005;

/** Default ramp for a live gain/pan/rate write, seconds. */
const RAMP = 0.02;

/**
 * Pooled `{gain, panner}` pairs kept alive at most. Past this a released pair is
 * simply dropped for the GC — the pool is a reuse cache, not a leak with a
 * friendly name, and a pile-up that briefly needed 60 voices should not hold 60
 * node pairs for the rest of the session.
 */
const POOL_MAX = 32;

/**
 * Default seconds for a music fade in or out. ⚠ A DEFAULT, NOT THE TUNING:
 * `RADIO_TUNE.fade` in `src/game/radio.js` is the authority and the wiring hands
 * it in. The number is spelled again here only so that a caller who says nothing
 * still gets a fade — see the header on why a hard cut on a stream is worse than
 * a hard cut on a one-shot.
 */
const MUSIC_FADE = 0.35;

/**
 * How far the element may already be from a requested position before
 * {@link api.playMusic} actually seeks, seconds. ⚠ THAT CALL IS RE-ASSERTABLE —
 * the wiring makes it again when an autoplay refusal is retried — and a seek
 * flushes the decoder audibly, so re-asserting a track must not stutter the track
 * it is re-asserting. A real move (a resume at 0:40) is orders of magnitude
 * bigger than this.
 */
const MUSIC_SEEK_EPS = 0.25;

/** @param {any} n @returns {boolean} A real, usable number (the `Wallet.js` guard). */
const usable = (n) => typeof n === 'number' && Number.isFinite(n);

/** @param {any} v @param {number} d @returns {number} `v` if usable, else `d`. */
const num = (v, d) => (usable(v) ? v : d);

/** @param {number} v @returns {number} */
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * THE ONLY WAY A LIVE `AudioParam` IS EVER WRITTEN. See the header: a step
 * change on a running source is a click.
 *
 * The `cancelScheduledValues` → `setValueAtTime(param.value)` → ramp idiom is
 * what makes a new ramp start from wherever the previous one had got to;
 * without the middle line a ramp interrupting a ramp restarts from the last
 * SCHEDULED point and jumps. (`cancelAndHoldAtTime` says this in one call and is
 * still not in every browser we target.)
 *
 * @param {any} param An `AudioParam`.
 * @param {number} target
 * @param {number} secs Ramp length; floored at {@link MIN_RAMP}.
 * @param {number} now `ctx.currentTime`.
 */
function rampTo(param, target, secs, now) {
  if (!param) return;
  const t = Math.max(MIN_RAMP, num(secs, RAMP));
  try {
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    param.linearRampToValueAtTime(target, now + t);
  } catch {
    /* a param on a context that closed under us is not worth a throw */
  }
}

/**
 * Find the seamless region of a decoded LOOPING buffer. See the header for why
 * this runs against the browser's own `AudioBuffer` and nowhere else.
 *
 * Only the first and last {@link LOOP_TRIM_MAX} are examined, and that is not an
 * optimisation but the definition: a margin longer than the clamp is not encoder
 * padding, it is the clip genuinely starting quietly, and trimming it would cut
 * the sound. If nothing crosses {@link SILENCE} inside the window the full clamp
 * is taken — the window really was silent.
 *
 * @param {any} buf An `AudioBuffer`.
 * @returns {{start: number, end: number}} Seconds, suitable for
 *   `source.loopStart` / `source.loopEnd`.
 */
export function scanLoopPoints(buf) {
  const rate = num(buf?.sampleRate, 44100);
  const n = Math.floor(num(buf?.length, 0));
  const full = { start: 0, end: n / rate };
  if (n < 4) return full;

  /** @type {Float32Array[]} */
  const chans = [];
  const nc = Math.max(1, Math.floor(num(buf.numberOfChannels, 1)));
  for (let c = 0; c < nc; c++) {
    try {
      chans.push(buf.getChannelData(c));
    } catch {
      /* a channel that will not hand over its data simply does not vote */
    }
  }
  if (!chans.length) return full;

  /** @param {number} i @returns {boolean} Any channel is above the floor at `i`. */
  const loud = (i) => {
    for (let c = 0; c < chans.length; c++) {
      const v = chans[c][i];
      if (v > SILENCE || v < -SILENCE) return true;
    }
    return false;
  };

  // Never let the two windows meet in the middle of a very short clip.
  const win = Math.max(1, Math.min(Math.floor(LOOP_TRIM_MAX * rate), n >> 1));

  let s = win; // clamp default: the whole window was silence
  for (let i = 0; i < win; i++) {
    if (loud(i)) {
      s = i;
      break;
    }
  }

  let e = n - win; // same, from the tail
  for (let i = n - 1; i >= n - win; i--) {
    if (loud(i)) {
      e = i + 1;
      break;
    }
  }

  // A degenerate answer (an all-but-silent clip) is worse than no answer.
  if (!(e > s + 1)) return full;
  return { start: s / rate, end: e / rate };
}

/**
 * @typedef {object} PlaySpec
 * @property {string} url Cache key AND fetch path. Must already be decoded.
 * @property {'sfx'|'ambient'|'ui'} [bus]
 * @property {number} [gain] Target linear gain, 0..n.
 * @property {number} [pan] -1..1.
 * @property {number} [rate] Playback rate multiplier.
 * @property {boolean} [loop]
 * @property {number} [fadeIn] Seconds. 0 starts at `gain` (correct for a
 *   one-shot: its own attack is the fade).
 * @property {(() => void)|null} [onEnded] Fired once, from `source.onended`,
 *   whether the source finished or was stopped.
 */

/**
 * @typedef {object} BackendVoice
 * @property {number} id
 * @property {string} url
 * @property {(v: number, ramp?: number) => void} setGain
 * @property {(p: number, ramp?: number) => void} setPan
 * @property {(r: number, ramp?: number) => void} setRate
 * @property {(fade?: number) => void} stop
 * @property {boolean} playing
 */

/**
 * The Web Audio backend. One per {@link module:engine/audio/Sfx} facade, which
 * is the only thing that should ever construct it.
 *
 * @param {object} [o]
 * @param {number} [o.concurrency] Decode jobs in flight. See
 *   {@link DECODE_CONCURRENCY}.
 * @param {Record<string, number>|null} [o.levels] Initial bus levels
 *   (`master`/`sfx`/`ambient`/`ui`/`music`). Applied to the gain nodes at the
 *   moment the context is built, so setting a level before `unlock()` is legal.
 * @param {(() => void)|null} [o.onReady] Fired ONCE, when the context is running
 *   and the first (`ui`) decode tier has been attempted. See `get ready`.
 * @param {((msg: string, err?: any) => void)|null} [o.log] Warnings. A missing
 *   or undecodable clip is never a throw — the game plays on in silence.
 * @returns {object}
 */
export function createAudioBackend(o = {}) {
  const {
    concurrency = DECODE_CONCURRENCY,
    levels = null,
    onReady = null,
    log = null,
  } = o ?? {};

  // ── the graph ───────────────────────────────────────────────────────────────
  /** @type {any} The `AudioContext`. ⚠ NULL until `unlock()`. See below. */
  let ctx = null;
  /** @type {any} */
  let masterNode = null;
  /**
   * @type {Record<string, any>} ⚠ `music` is a BUS but NOT a {@link TIERS} entry:
   * nothing is ever decoded onto it, so it has no place in the decode order and
   * `play()` cannot target it. See the header.
   */
  const busNode = { sfx: null, ambient: null, ui: null, music: null };

  /**
   * Bus levels, held here whether or not a context exists. The graph is a
   * PROJECTION of this object, which is what lets the settings UI move a slider
   * on the title screen before anybody has clicked anything.
   */
  const level = {
    master: clamp01(num(levels?.master, 1)),
    sfx: clamp01(num(levels?.sfx, 1)),
    ambient: clamp01(num(levels?.ambient, 1)),
    ui: clamp01(num(levels?.ui, 1)),
    music: clamp01(num(levels?.music, 1)),
  };

  // ── the cache ───────────────────────────────────────────────────────────────
  /** @type {Map<string, any>} url → `AudioBuffer`. */
  const buffers = new Map();
  /** @type {Map<string, {start: number, end: number}>} url → seamless region. */
  const loopPoints = new Map();
  /** @type {Array<{url: string, loop: boolean, tier: number}>} The decode queue. */
  const queue = [];
  /** @type {Set<string>} Queued or in flight — the dedupe. */
  const claimed = new Set();
  /** Items left per tier, so `ready` can mean "the UI tier has been attempted". */
  const tierLeft = [0, 0, 0];
  let inflight = 0;
  let decoded = 0;
  let failed = 0;

  // ── the pool ────────────────────────────────────────────────────────────────
  /** @type {Array<{gain: any, panner: any}>} Free pairs. */
  const pool = [];
  /** @type {Set<any>} Live voices, for `stopAll` and `dispose`. */
  const voices = new Set();
  let nextId = 1;

  // ── the music stream ────────────────────────────────────────────────────────
  /** @type {any} The one `HTMLAudioElement`. ⚠ Built once and kept for ever. */
  let musicEl = null;
  /** @type {any} Its `MediaElementAudioSourceNode`. ⚠ ONE PER ELEMENT, EVER. */
  let musicSrc = null;
  /** ⚠ Latched BEFORE the `createMediaElementSource` call, so a throw never retries. */
  let musicWired = false;
  /** @type {any} The TRACK gain — the radio's own level and its fades, under `busNode.music`. */
  let musicTrack = null;
  /** @type {string|null} The url we were HANDED. `el.src` is absolutised by the browser. */
  let musicUrl = null;
  /** The track gain's target. Sticky: it is a property of the station, not of one track. */
  let musicGainTarget = 1;
  /** The game wants music. Survives an autoplay refusal, so `unlock()` can re-assert it. */
  let musicWanted = false;
  /** @type {number|null} A seek the element was not ready for. See {@link onMusicMeta}. */
  let musicSeek = null;
  /** @type {any} The pause-after-fade timer. See {@link api.stopMusic}. */
  let musicPauseTimer = null;

  // ── lifecycle ───────────────────────────────────────────────────────────────
  let disposed = false;
  let readyFired = false;
  /**
   * The GAME asked for silence (a pause, a menu), as opposed to the tab being
   * hidden. ⚠ Without this flag a player who alt-tabs away from a paused game
   * and back again comes back to a game that is paused and audible.
   */
  let userSuspended = false;
  /** @type {(() => void)|null} */
  let offVisibility = null;

  /** @param {string} m @param {any} [e] */
  const warn = (m, e) => {
    if (typeof log === 'function') log(m, e);
  };

  /** @returns {number} The context clock, or 0 before there is one. */
  const nowT = () => (ctx ? ctx.currentTime : 0);

  // ── the context ─────────────────────────────────────────────────────────────

  /**
   * Builds the context and the four bus gains, ONCE.
   *
   * ⚠ **LAZY, AND CALLED ONLY FROM `unlock()`.** A context constructed at page
   * load, before any user gesture, is born `suspended` on every browser that
   * implements the autoplay policy and warns about it in the console on some of
   * them — once per page load, for a game that may never make a sound because
   * the player is in the menu. Constructing it inside the gesture handler means
   * the very first thing that happens to it is a legal `resume()`.
   *
   * @returns {any} The context, or null where there is no Web Audio at all
   *   (node, a suite, a locked-down embed). ⚠ EVERY method below tolerates null.
   */
  const ensureCtx = () => {
    if (ctx || disposed) return ctx;
    const AC = globalThis.AudioContext ?? globalThis.webkitAudioContext ?? null;
    if (!AC) {
      warn('audio: no AudioContext in this environment; the game runs silent');
      return null;
    }
    try {
      ctx = new AC({ latencyHint: 'interactive' });
    } catch (e) {
      warn('audio: AudioContext construction failed', e);
      ctx = null;
      return null;
    }

    // ⚠ Bare `.value =` is legal HERE and nowhere else in this file: the six
    // nodes built in this block — master, the three tier buses, the music bus and
    // the music track gain — have never been connected to a started source, so
    // there is no waveform for a step to be a discontinuity in. Every later write
    // to any of them goes through `rampTo`.
    masterNode = ctx.createGain();
    masterNode.gain.value = level.master;
    masterNode.connect(ctx.destination);
    for (const b of TIERS) {
      const g = ctx.createGain();
      g.gain.value = level[b];
      g.connect(masterNode);
      busNode[b] = g;
    }

    // ⚠ The music bus is built HERE and not in the loop above, because `TIERS` is
    // the DECODE ORDER and nothing is ever decoded onto this one. Two gains: the
    // bus is the player's slider, the track gain is the radio's own level and its
    // fades, and neither may overwrite the other. The track gain starts at ZERO —
    // every `playMusic` ramps UP from wherever it stands, so the first track fades
    // in exactly like the tenth.
    const mb = ctx.createGain();
    mb.gain.value = level.music;
    mb.connect(masterNode);
    busNode.music = mb;
    musicTrack = ctx.createGain();
    musicTrack.gain.value = 0;
    musicTrack.connect(mb);

    // ⚠ A game that keeps its sirens running in a background tab is a bug
    // report. Registered here rather than at construction so a backend that
    // never unlocks touches no globals at all; removed in `dispose`.
    const doc = globalThis.document ?? null;
    if (doc?.addEventListener) {
      const onVis = () => {
        if (disposed || !ctx) return;
        if (doc.hidden) {
          try {
            ctx.suspend();
          } catch {
            /* closing */
          }
        } else if (!userSuspended) {
          try {
            ctx.resume();
          } catch {
            /* closing */
          }
        }
      };
      doc.addEventListener('visibilitychange', onVis);
      offVisibility = () => doc.removeEventListener('visibilitychange', onVis);
    }

    return ctx;
  };

  // ── loading ─────────────────────────────────────────────────────────────────

  /**
   * `decodeAudioData` in both of its shapes. Safari returned only the callback
   * form for years and still accepts it; the promise form is the modern one.
   * Resolving from whichever arrives first covers both without sniffing.
   *
   * @param {ArrayBuffer} ab
   * @returns {Promise<any>}
   */
  const decode = (ab) =>
    new Promise((resolve, reject) => {
      let p = null;
      try {
        p = ctx.decodeAudioData(ab, resolve, reject);
      } catch (e) {
        reject(e);
        return;
      }
      if (p && typeof p.then === 'function') p.then(resolve, reject);
    });

  /**
   * Has the first tier finished? ⚠ THE DEFINITION OF `ready`, and it is
   * deliberately NOT "everything is decoded": the rain bed is 22 s of MP3 and
   * the two sirens are 5 s each, so waiting for them would hold the flag for
   * most of a second after the game is already audible. Nothing gates on
   * `ready` — `play()` asks the cache for its own buffer — so this is a readout,
   * and the useful thing to read out is "the phone can ring".
   */
  const checkReady = () => {
    if (readyFired || disposed) return;
    if (!ctx || ctx.state !== 'running') return;
    if (tierLeft[0] > 0) return;
    readyFired = true;
    try {
      onReady?.();
    } catch (e) {
      warn('audio: onReady threw', e);
    }
  };

  /**
   * One fetch + decode. Never rejects: a 404 or a corrupt clip costs a warning
   * and one silent slug, never a broken game.
   * @param {{url: string, loop: boolean, tier: number}} item
   */
  const loadOne = async (item) => {
    inflight++;
    try {
      const res = await globalThis.fetch(item.url);
      if (!res?.ok) throw new Error(`HTTP ${res?.status ?? '?'}`);
      const ab = await res.arrayBuffer();
      if (disposed || !ctx) return;
      const buf = await decode(ab);
      if (disposed) return;
      buffers.set(item.url, buf);
      // ⚠ ONLY looping clips are scanned. A one-shot's padding is inaudible —
      // it is a few silent milliseconds before an attack, once.
      if (item.loop) loopPoints.set(item.url, scanLoopPoints(buf));
      decoded++;
    } catch (e) {
      failed++;
      warn(`audio: failed to load ${item.url}`, e);
    } finally {
      inflight--;
      tierLeft[item.tier] = Math.max(0, tierLeft[item.tier] - 1);
      pump();
      checkReady();
    }
  };

  /**
   * Starts as many queued loads as the concurrency cap allows.
   * ⚠ A no-op before the context exists — there is nothing to decode INTO — so
   * `preload()` may safely be called at construction and simply queues.
   */
  const pump = () => {
    if (disposed || !ctx) return;
    const cap = Math.max(1, Math.floor(num(concurrency, DECODE_CONCURRENCY)));
    while (inflight < cap && queue.length) {
      const item = queue.shift();
      if (item) loadOne(item);
    }
  };

  // ── voices ──────────────────────────────────────────────────────────────────

  /**
   * A `{gain, panner}` pair, pooled. See the header: the source is the only node
   * the spec forces us to rebuild.
   * @returns {{gain: any, panner: any}|null}
   */
  const takePair = () => {
    const hit = pool.pop();
    if (hit) return hit;
    try {
      const gain = ctx.createGain();
      // ⚠ `StereoPannerNode`, never `PannerNode` — see the header. The fallback
      // for a browser without one is a pass-through gain and no panning, which
      // costs the stereo image and nothing else.
      const panner =
        typeof ctx.createStereoPanner === 'function' ? ctx.createStereoPanner() : ctx.createGain();
      gain.connect(panner);
      return { gain, panner };
    } catch (e) {
      warn('audio: node allocation failed', e);
      return null;
    }
  };

  /** @param {{gain: any, panner: any}} pair */
  const givePair = (pair) => {
    try {
      pair.panner.disconnect();
    } catch {
      /* already gone */
    }
    if (pool.length >= POOL_MAX || disposed) {
      try {
        pair.gain.disconnect();
      } catch {
        /* already gone */
      }
      return;
    }
    // Not a live voice: the panner is disconnected from the graph, so there is
    // no signal for a step to be heard in.
    try {
      const t = nowT();
      pair.gain.gain.cancelScheduledValues(t);
      pair.gain.gain.setValueAtTime(1, t);
      if (pair.panner.pan) {
        pair.panner.pan.cancelScheduledValues(t);
        pair.panner.pan.setValueAtTime(0, t);
      }
    } catch {
      /* closing */
    }
    pool.push(pair);
  };

  // ── the music stream ────────────────────────────────────────────────────────

  /**
   * Re-asserts a seek the element was not ready for. Setting `currentTime` before
   * the metadata has landed is SPECIFIED to become the "default playback start
   * position", but it has historically thrown instead on some browsers and been
   * quietly ignored on others — so {@link seekMusic} remembers the ask and this
   * finishes the job the moment the element can honour it.
   */
  const onMusicMeta = () => {
    if (disposed || musicSeek === null || !musicEl) return;
    const at = musicSeek;
    musicSeek = null;
    try {
      musicEl.currentTime = at;
    } catch (e) {
      warn('audio: music seek after metadata failed', e);
    }
  };

  /**
   * The one element. ⚠ DETACHED from the document on purpose: nothing needs to
   * SEE it, and a stray `<audio>` in the tree is a control some browsers decide
   * to render. Same-origin (`/radio/...`), so no `crossOrigin` — a cross-origin
   * element without CORS makes `createMediaElementSource` output silence.
   *
   * @returns {any|null} Null where there is no DOM at all (node, a suite).
   */
  const ensureMusicEl = () => {
    if (musicEl || disposed) return musicEl;
    const doc = globalThis.document ?? null;
    if (typeof doc?.createElement !== 'function') return null;
    try {
      const el = doc.createElement('audio');
      el.preload = 'auto';
      el.loop = false;
      // ⚠ THE ONLY WRITE TO `.volume` THERE WILL EVER BE — see the header. The
      // gain graph is the volume, because a gain can be ramped and this cannot.
      el.volume = 1;
      el.addEventListener('loadedmetadata', onMusicMeta);
      musicEl = el;
    } catch (e) {
      warn('audio: could not create the music element', e);
      return null;
    }
    return musicEl;
  };

  /**
   * Routes the element into the graph. ⚠ **ONCE PER ELEMENT, EVER** — see the
   * header. A no-op before `unlock()` (there is no context to build the node on),
   * which is exactly why `unlock()` calls it again.
   */
  const wireMusic = () => {
    if (disposed || musicWired || !ctx || !musicEl || !musicTrack) return;
    musicWired = true; // ⚠ BEFORE the call: a call that threw must not be retried
    try {
      musicSrc = ctx.createMediaElementSource(musicEl);
      musicSrc.connect(musicTrack);
    } catch (e) {
      musicSrc = null;
      warn('audio: createMediaElementSource failed; the radio stays silent', e);
    }
  };

  /**
   * ⚠ Only ever called on a WIRED element — an unrouted one plays straight past
   * `masterGain` and would ignore both the master slider and the mute. The
   * promise is caught and dropped, never awaited: a rejection is the autoplay
   * policy, not an error, and `musicWanted` is what gets it retried.
   */
  const startMusicEl = () => {
    if (disposed || !musicEl || !musicSrc) return;
    try {
      const p = musicEl.play();
      if (p && typeof p.catch === 'function') {
        p.catch((e) => warn('audio: music play refused (no user gesture yet?)', e));
      }
    } catch (e) {
      warn('audio: music play threw', e);
    }
  };

  /** Drops a pending pause so it cannot land in the middle of the next track. */
  const clearMusicPause = () => {
    if (musicPauseTimer === null) return;
    try {
      globalThis.clearTimeout?.(musicPauseTimer);
    } catch {
      /* an environment with no timers had nothing scheduled either */
    }
    musicPauseTimer = null;
  };

  /**
   * @param {number} at Seconds.
   * @param {boolean} force Seek even when we are already near `at`. True on a
   *   track CHANGE, where the element's old position means nothing. See
   *   {@link MUSIC_SEEK_EPS} for why the other case is guarded.
   */
  const seekMusic = (at, force) => {
    if (!musicEl) return;
    if (!force && Math.abs(num(musicEl.currentTime, 0) - at) <= MUSIC_SEEK_EPS) return;
    musicSeek = at;
    try {
      musicEl.currentTime = at;
      // A ready element really did seek. An unready one only recorded a default
      // start position, and `onMusicMeta` re-asserts that where it does not stick.
      if (num(musicEl.readyState, 0) > 0) musicSeek = null;
    } catch {
      /* not ready and not tolerant of being asked — `onMusicMeta` finishes it */
    }
  };

  const api = {
    name: 'audioBackend',

    // ── lifecycle ─────────────────────────────────────────────────────────────

    /**
     * Builds the context (see {@link ensureCtx}) and resumes it. ⚠ CALL FROM A
     * USER GESTURE — a `resume()` outside one is rejected by the autoplay policy
     * and the promise below simply resolves with the context still suspended,
     * which is the correct outcome: nothing throws, nothing plays, and the next
     * real gesture fixes it.
     *
     * Also drains anything `preload()` queued before there was a context.
     *
     * @returns {Promise<void>} NEVER rejects.
     */
    async unlock() {
      if (disposed) return;
      const c = ensureCtx();
      if (!c) return;
      userSuspended = false;
      if (c.state === 'suspended') {
        try {
          await c.resume();
        } catch (e) {
          warn('audio: resume refused (no user gesture yet?)', e);
        }
      }
      if (disposed) return;
      pump();
      checkReady();
      // ⚠ The radio may already have been handed a track, back when there was no
      // context to route it through and therefore no legal way to start it. See
      // the header: the intent is remembered rather than lost, and this is where
      // it is honoured.
      wireMusic();
      if (musicWanted) {
        if (musicTrack) rampTo(musicTrack.gain, musicGainTarget, MUSIC_FADE, nowT());
        startMusicEl();
      }
    },

    /**
     * The GAME wants silence. Distinct from the tab being hidden — see
     * {@link userSuspended}.
     */
    suspend() {
      userSuspended = true;
      try {
        ctx?.suspend?.();
      } catch {
        /* closing */
      }
    },

    /** The game wants sound back. */
    resume() {
      userSuspended = false;
      if (disposed) return;
      try {
        ctx?.resume?.();
      } catch {
        /* closing */
      }
      checkReady();
    },

    // ── loading ───────────────────────────────────────────────────────────────

    /**
     * Queue clips for load. Safe at any time, including before `unlock()`
     * (nothing decodes until there is a context) and after decode has begun
     * (already-claimed URLs are skipped).
     *
     * ⚠ The queue is SORTED BY TIER, not by call order: `ui` → `sfx` →
     * `ambient`. See {@link TIERS}.
     *
     * @param {Array<{url: string, bus?: string, loop?: boolean}>} items
     */
    preload(items) {
      if (disposed || !Array.isArray(items)) return;
      let added = false;
      for (const it of items) {
        const url = typeof it?.url === 'string' ? it.url : '';
        if (!url || claimed.has(url) || buffers.has(url)) continue;
        // ⚠ An unknown bus falls back to `sfx`, NOT to index 0 — a `Math.max(0,
        // indexOf)` would silently promote every typo to the `ui` tier, which is
        // the one tier `ready` waits for.
        const found = TIERS.indexOf(it?.bus);
        const tier = found < 0 ? TIERS.indexOf('sfx') : found;
        claimed.add(url);
        queue.push({ url, loop: !!it?.loop, tier });
        tierLeft[tier]++;
        added = true;
      }
      if (added) queue.sort((a, b) => a.tier - b.tier);
      pump();
    },

    /**
     * @param {string} url
     * @returns {any|null} The decoded buffer, or null if it is missing, still in
     *   flight or failed. ⚠ The caller's cue to return -1 rather than to wait:
     *   `play()` never awaits.
     */
    buffer(url) {
      return buffers.get(url) ?? null;
    },

    /**
     * @param {string} url
     * @returns {{start: number, end: number}|null} The measured seamless region,
     *   for looping buffers only.
     */
    loopPoints(url) {
      return loopPoints.get(url) ?? null;
    },

    // ── levels ────────────────────────────────────────────────────────────────

    /**
     * @param {string} bus `'master'|'sfx'|'ambient'|'ui'|'music'`.
     * @param {number} v 0..1.
     * @param {number} [ramp] Seconds. ⚠ Floored at {@link MIN_RAMP} — a slider
     *   dragged fast is a stream of small steps, and a stream of small steps
     *   with no ramp is a stream of small clicks.
     */
    setBusLevel(bus, v, ramp = 0.03) {
      if (disposed) return;
      // ⚠ `music` is named separately rather than being added to {@link TIERS}:
      // that list is the DECODE ORDER, and a streamed track is never decoded.
      if (bus !== 'master' && bus !== 'music' && !TIERS.includes(bus)) return;
      level[bus] = clamp01(num(v, level[bus]));
      const node = bus === 'master' ? masterNode : busNode[bus];
      if (!ctx || !node) return; // the graph is a projection; it will pick this up when it exists
      rampTo(node.gain, level[bus], ramp, nowT());
    },

    /** @param {string} bus @returns {number} */
    busLevel(bus) {
      return num(level[bus], 0);
    },

    // ── playing ───────────────────────────────────────────────────────────────

    /**
     * Start one voice. Synchronous, never throws, returns null when it cannot —
     * a suspended context, an undecoded buffer, an exhausted node allocator.
     *
     * @param {PlaySpec} spec
     * @returns {BackendVoice|null}
     */
    play(spec) {
      if (disposed || !ctx || ctx.state === 'closed') return null;
      const url = typeof spec?.url === 'string' ? spec.url : '';
      const buf = buffers.get(url);
      if (!buf) return null;

      const busName = TIERS.includes(spec?.bus) ? spec.bus : 'sfx';
      const dest = busNode[busName];
      if (!dest) return null;

      const pair = takePair();
      if (!pair) return null;

      /** @type {any} */
      let source = null;
      try {
        source = ctx.createBufferSource();
        source.buffer = buf;
      } catch (e) {
        warn('audio: createBufferSource failed', e);
        givePair(pair);
        return null;
      }

      const t = nowT();
      const target = Math.max(0, num(spec?.gain, 1));
      const fadeIn = Math.max(0, num(spec?.fadeIn, 0));
      const loop = !!spec?.loop;

      // ⚠ Bare assignment is legal here: nothing is connected to a started
      // source yet, so there is no waveform for a step to interrupt. The fade-in
      // below is a SCHEDULED ramp from this assigned value.
      pair.gain.gain.value = fadeIn > 0 ? 0 : target;
      if (pair.panner.pan) pair.panner.pan.value = Math.max(-1, Math.min(1, num(spec?.pan, 0)));
      source.playbackRate.value = Math.max(0.02, num(spec?.rate, 1));

      if (loop) {
        source.loop = true;
        // The measured seamless region — see the header. A buffer that was never
        // scanned (a one-shot slug asked to loop) simply loops end to end.
        const lp = loopPoints.get(url);
        if (lp && lp.end > lp.start) {
          source.loopStart = lp.start;
          source.loopEnd = lp.end;
        }
      }

      source.connect(pair.gain);
      pair.panner.connect(dest);

      if (fadeIn > 0) rampTo(pair.gain.gain, target, fadeIn, t);

      const id = nextId++;
      let ended = false;
      let stopAt = Infinity;

      /** Hands the nodes back exactly once, whatever ended the voice. */
      const finish = () => {
        if (ended) return;
        ended = true;
        voices.delete(voice);
        try {
          source.onended = null;
          source.disconnect();
        } catch {
          /* already gone */
        }
        givePair(pair);
        try {
          spec?.onEnded?.();
        } catch (e) {
          warn('audio: onEnded threw', e);
        }
      };

      /** @type {BackendVoice} */
      const voice = {
        id,
        url,

        /**
         * @param {number} v
         * @param {number} [ramp] ⚠ Always a ramp. See the header.
         */
        setGain(v, ramp = RAMP) {
          if (ended || !ctx) return;
          rampTo(pair.gain.gain, Math.max(0, num(v, 0)), ramp, nowT());
        },

        /** @param {number} p -1..1 @param {number} [ramp] */
        setPan(p, ramp = RAMP) {
          if (ended || !ctx || !pair.panner.pan) return;
          rampTo(pair.panner.pan, Math.max(-1, Math.min(1, num(p, 0))), ramp, nowT());
        },

        /**
         * @param {number} r @param {number} [ramp] A pitch STEP is not a click,
         *   but it is a glitch on a sustained loop, and a car engine's rate moves
         *   every frame — so this ramps too.
         */
        setRate(r, ramp = RAMP) {
          if (ended || !ctx) return;
          rampTo(source.playbackRate, Math.max(0.02, num(r, 1)), ramp, nowT());
        },

        /**
         * Fade to silence and stop. ⚠ **THERE IS NO INSTANT STOP.** A source cut
         * mid-waveform is the loudest click this system can make, so even
         * `stop(0)` takes {@link MIN_RAMP} × 2. `dispose()` is the one exception,
         * and only because the context is closing in the same breath.
         *
         * @param {number} [fade] Seconds.
         */
        stop(fade = 0) {
          if (ended || !ctx) return;
          const f = Math.max(MIN_RAMP * 2, num(fade, 0));
          const at = nowT() + f;
          if (at >= stopAt) return; // a longer stop already scheduled; keep the shorter
          stopAt = at;
          rampTo(pair.gain.gain, 0, f, nowT());
          try {
            source.stop(at + 0.005);
          } catch {
            // Already stopped, or never started. `onended` still arrives.
          }
        },

        get playing() {
          return !ended;
        },
      };

      source.onended = finish;
      voices.add(voice);

      try {
        source.start(t);
      } catch (e) {
        warn('audio: source.start failed', e);
        finish();
        return null;
      }

      return voice;
    },

    /**
     * @param {number} [fade] Seconds.
     * ⚠ Iterates a SNAPSHOT: `finish()` mutates the set (the `clearActors`
     * lesson from `Missions.js`).
     * ⚠ MUSIC IS NOT A VOICE and is deliberately untouched — the station is
     * still on air, and silencing the world's sound effects is not a reason to
     * take the radio off it. `stopMusic` is the only exit.
     */
    stopAll(fade = 0.05) {
      for (const v of [...voices]) v.stop(fade);
    },

    // ── music ─────────────────────────────────────────────────────────────────

    /**
     * Point the one streaming element at `url`, seek to `offset` and play, fading
     * the track gain in. ⚠ **NEVER THROWS AND NEVER AWAITS** — a refused `play()`
     * costs a warning and nothing else, and the intent survives it so `unlock()`
     * can re-assert. Safe before there is a context: everything but the actual
     * `play()` happens anyway, and `unlock()` finishes the job.
     *
     * ⚠ Changing track is `el.src`. It is never a new element and never a second
     * `createMediaElementSource` — see the header.
     *
     * @param {string} url Already `base + file`. This file derives no paths.
     * @param {number} [offset] Seconds into the track. A streamed element seeks
     *   for free, which is the whole "resume where the station got to" feature.
     * @param {object} [opts]
     * @param {number} [opts.gain] New track gain. STICKY — it survives every
     *   later `playMusic` that does not name one.
     * @param {number} [opts.fade] Fade-in seconds. See {@link MUSIC_FADE}.
     */
    playMusic(url, offset = 0, opts) {
      if (disposed) return;
      const u = typeof url === 'string' ? url : '';
      if (!u) return;
      const el = ensureMusicEl();
      if (!el) return;

      clearMusicPause(); // a pause left over from a stop must not land mid-track
      musicWanted = true;
      musicGainTarget = Math.max(0, num(opts?.gain, musicGainTarget));

      const changing = u !== musicUrl;
      if (changing) {
        musicUrl = u;
        try {
          // Assigning `src` invokes the media load algorithm on its own; the
          // seek below rides the "default playback start position" it resets to.
          el.src = u;
        } catch (e) {
          warn(`audio: could not point the music element at ${u}`, e);
        }
      }
      seekMusic(Math.max(0, num(offset, 0)), changing);

      wireMusic(); // a no-op before `unlock()`, which calls it again
      if (musicTrack) {
        rampTo(musicTrack.gain, musicGainTarget, num(opts?.fade, MUSIC_FADE), nowT());
      }
      startMusicEl();
    },

    /**
     * Fade the music out and pause it. ⚠ **DOES NOT CLEAR THE POSITION OR THE
     * URL.** The station is still on air; the player has merely got out of the
     * car, and the next `playMusic` seeks to wherever the model says the track
     * has reached in the meantime.
     *
     * ⚠ The element is paused on a TIMER, after the ramp has landed. Pausing it
     * in the same breath as scheduling the fade is precisely the cut the fade
     * exists to avoid — and, as with a voice, there is no instant stop.
     *
     * @param {number} [fade] Seconds. Floored at `MIN_RAMP * 2`.
     */
    stopMusic(fade = MUSIC_FADE) {
      if (disposed) return;
      musicWanted = false;
      const f = Math.max(MIN_RAMP * 2, num(fade, MUSIC_FADE));
      if (musicTrack) rampTo(musicTrack.gain, 0, f, nowT());
      if (!musicEl) return;
      clearMusicPause();
      const el = musicEl;
      musicPauseTimer =
        globalThis.setTimeout?.(
          () => {
            musicPauseTimer = null;
            if (disposed) return;
            try {
              el.pause();
            } catch {
              /* an element the document already took away */
            }
          },
          (f + 0.02) * 1000,
        ) ?? null;
    },

    /**
     * The radio's own level, under the `music` bus and the master. ⚠ Ramped like
     * everything else, and STICKY: this is `RADIO_TUNE.gain`, a property of the
     * station rather than an argument to one track, so `playMusic` fades TO it
     * rather than replacing it.
     * @param {number} g @param {number} [ramp] Seconds.
     */
    setMusicGain(g, ramp = RAMP) {
      if (disposed) return;
      musicGainTarget = Math.max(0, num(g, musicGainTarget));
      if (musicTrack) rampTo(musicTrack.gain, musicGainTarget, ramp, nowT());
    },

    /** @returns {number} Seconds into the current track, or 0 when there is none. */
    get musicPosition() {
      return musicEl ? num(musicEl.currentTime, 0) : 0;
    },

    /**
     * @returns {boolean} ⚠ BOTH flags are tested: reaching the end of a track
     *   fires `ended` but does NOT set `paused`, so `!paused` alone reports a
     *   finished track as still playing.
     */
    get musicPlaying() {
      return !!musicEl && !musicEl.paused && !musicEl.ended;
    },

    /** @returns {string|null} The url we were HANDED — `el.src` is absolutised. */
    get musicSrc() {
      return musicUrl;
    },

    // ── readouts ──────────────────────────────────────────────────────────────

    /** @returns {any|null} The context, for a review scene. Nothing else needs it. */
    get ctx() {
      return ctx;
    },

    /** @returns {number|null} `ctx.currentTime`, or null before there is one. */
    get time() {
      return ctx ? ctx.currentTime : null;
    },

    /** @returns {string|null} `'suspended'|'running'|'closed'`, or null. */
    get state() {
      return ctx?.state ?? null;
    },

    /** @returns {boolean} */
    get running() {
      return ctx?.state === 'running';
    },

    /** @returns {boolean} Context running AND the `ui` tier attempted. See {@link checkReady}. */
    get ready() {
      return !disposed && ctx?.state === 'running' && tierLeft[0] === 0;
    },

    /**
     * @returns {{decoded: number, failed: number, pending: number, live: number,
     *   pool: number, ctx: string|null}}
     */
    stats() {
      return {
        decoded,
        failed,
        pending: queue.length + inflight,
        live: voices.size,
        pool: pool.length,
        ctx: ctx?.state ?? null,
      };
    },

    /**
     * Idempotent. Stops every voice HARD (the one place that is allowed to: the
     * context is closing in the same breath, so there is nothing left to hear
     * the click in), pauses and empties the music element, drops the visibility
     * listener and releases the graph.
     */
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const v of [...voices]) {
        try {
          v.stop(0);
        } catch {
          /* going away anyway */
        }
      }
      voices.clear();
      pool.length = 0;
      queue.length = 0;
      claimed.clear();
      buffers.clear();
      loopPoints.clear();

      clearMusicPause();
      musicWanted = false;
      if (musicEl) {
        try {
          musicEl.removeEventListener('loadedmetadata', onMusicMeta);
          musicEl.pause();
          // ⚠ `removeAttribute` then `load()`, NEVER `src = ''` — the empty
          // string resolves against the page url, so the browser goes and fetches
          // the document itself and tries to decode it as audio. `load()` is what
          // actually aborts a download that is still in flight.
          musicEl.removeAttribute('src');
          musicEl.load();
        } catch {
          /* an element the document already took away */
        }
      }
      try {
        musicSrc?.disconnect();
        musicTrack?.disconnect();
      } catch {
        /* closing */
      }
      musicEl = null;
      musicSrc = null;
      musicTrack = null;
      musicUrl = null;

      try {
        offVisibility?.();
      } catch {
        /* a document that went away first is not our problem */
      }
      offVisibility = null;
      try {
        masterNode?.disconnect();
      } catch {
        /* closing */
      }
      const dying = ctx;
      ctx = null;
      masterNode = null;
      busNode.sfx = busNode.ambient = busNode.ui = busNode.music = null;
      try {
        dying?.close?.();
      } catch {
        /* a context that closed itself is the outcome we wanted */
      }
    },
  };

  return api;
}
